import React from 'react'
import SearchTilePropertyDetails from 'components/OfferList/OfferListTiles/SearchOfferTiles/TileSections/SearchTilePropertyDetails'
import moment from 'moment'
import styled from 'styled-components'
import { rem } from 'polished'
import TextButton from 'components/Luxkit/Button/TextButton'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { useIsMobileScreen } from 'lib/web/deviceUtils'
import IconButton from 'components/Luxkit/Button/IconButton'
import LineMultiplyIcon from 'components/Luxkit/Icons/line/LineMultiplyIcon'
import AspectRatio from 'components/utils/AspectRatio'
import ImageCarousel from 'components/Luxkit/Carousel/ImageCarousel'
import BookmarkButton from 'tripPlanner/components/Bookmark/BookmarkButton'
import { accountAccessShowLogin } from 'actions/UiActions'
import WalledContent from 'components/Common/WalledContent'
import LinePricetagAltIcon from 'components/Luxkit/Icons/line/LinePricetagAltIcon'
import CaptionTextBlock from 'components/Luxkit/TextBlocks/CaptionTextBlock'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { ImageParams } from 'components/Common/ResponsiveImage'
import ProductPaletteProvider, { ProductPalette } from 'contexts/ProductPaletteContext'
import { isUltraLuxOffer } from 'lib/offer/offerUtils'
import OfferTilePricing from 'components/OfferList/OfferListTiles/Support/OfferTilePricing'
import SoldOutMessage from 'components/OfferList/OfferListTiles/Support/SoldOutMessage'
import Group from 'components/utils/Group'
import { isLuxuryPlusEnabled } from 'loyalty/selectors/featureToggle'

export const DESKTOP_TILE_WIDTH = 400

const imageParams: ImageParams = {
  mobileAspectRatio: '17:6',
  mobileWidth: '100vw',
  tabletAspectRatio: '2:1',
  tabletWidth: '400px',
}

const Root = styled.div`
  position: relative;
  pointer-events: all;
  background-color: ${({ theme }) => theme.palette.neutral.default.eight};
  display: flex;
  flex-direction: column;
  width: calc(
    100vw - 48px
  ); /* Mobile-only sizing, no need to scale with rems */
  border-radius: ${(props) => props.theme.borderRadius.S};
  box-shadow: 0px 0px 8px rgba(0, 0, 0, 0.15);
  ${mediaQueryUp.tablet} {
    max-width: ${rem(DESKTOP_TILE_WIDTH)};
  }
`

const BookmarkContainer = styled.div`
  position: absolute;
  top: ${rem(8)};
  right: ${rem(8)};
  z-index: 1;
`

const Container = styled.div`
  padding: ${rem(16)};
  padding-top: ${rem(12)};
  > * + * {
    padding-top: ${rem(8)};
  }
`

const ButtonsContainer = styled.div`
  & > * + * {
    margin-left: ${rem(8)};
  }
`

const StyledIconButton = styled(IconButton)`
  position: absolute;
  top: ${rem(8)};
  left: ${rem(8)};
  z-index: 1;
  border-color: ${(props) => props.theme.palette.neutral.default.eight};
  background-color: ${(props) => props.theme.palette.neutral.default.eight};
  width: ${rem(32)};
  height: ${rem(32)};
`

interface Props {
  onClose?: () => void;
  offer: App.Offer | App.OfferSummary;
  location: string;
  checkInDate?: moment.Moment;
  filters: App.OfferListFilters;
  offerUrl: string;
  bestPricePackage?: App.Package;
  bestPriceForDates?: App.OfferAvailableRate;
  fetchingPrice: boolean;
}

function LeMapSearchTile(props: Props) {
  const {
    onClose,
    offer,
    location,
    checkInDate,
    filters,
    offerUrl,
    bestPricePackage,
    bestPriceForDates,
    fetchingPrice,
  } = props
  const isLuxPlusEnabled = useAppSelector(state => isLuxuryPlusEnabled(state))
  const isMobile = useIsMobileScreen()
  const cancellationPolicyType =
    bestPricePackage?.roomRate?.cancellationPolicy?.type

  const dispatch = useAppDispatch()
  const loginHandler: React.MouseEventHandler<HTMLButtonElement> = (e) => {
    e.preventDefault()
    dispatch(accountAccessShowLogin())
  }

  const paletteValue: ProductPalette = isLuxPlusEnabled && !!offer.loyalty?.access ? 'loyalty' : isUltraLuxOffer(offer) ? 'ultralux' : 'default'
  const hasDates = !!checkInDate
  const { images } = offer

  return (
    <Root>
      <div>
        <AspectRatio ratio="16:9" tabletRatio="2:1">
          {onClose && (
            <StyledIconButton
              size="small"
              shape="circle"
              kind="secondary"
              onClick={(e) => {
                e.preventDefault()
                onClose()
              }}
            >
              <LineMultiplyIcon />
            </StyledIconButton>
          )}
          <BookmarkContainer>
            <BookmarkButton offer={offer} />
          </BookmarkContainer>
          <ImageCarousel
            images={images}
            imageParams={imageParams}
            eagerLoadFirstImage
          />
        </AspectRatio>
      </div>
      <Container>
        <SearchTilePropertyDetails
          location={location}
          offer={offer}
          checkInDate={checkInDate}
          filters={filters}
          offerUrl={offerUrl}
          bestPricePackage={bestPricePackage}
          showCompact
          maxInclusions={isMobile ? 2 : 4}
          cancellationPolicyType={cancellationPolicyType}
          maxInclusionLines={5}
        />
        <ProductPaletteProvider palette={paletteValue}>
          <WalledContent
            enforced={offer.walledGarden}
            wall={
              <>
                <CaptionTextBlock
                  variant="medium"
                  weight="bold"
                  startIcon={<LinePricetagAltIcon />}
                >
                  Members get exclusive rates
                </CaptionTextBlock>
                <ButtonsContainer>
                  <TextButton
                    kind="secondary"
                    onClick={loginHandler}
                    size="small"
                  >
                    Log In
                  </TextButton>
                  <TextButton kind="primary" onClick={loginHandler} size="small">
                    Sign up for free
                  </TextButton>
                </ButtonsContainer>
              </>
            }
          >
            <Group direction="horizontal" horizontalAlign="space-between" verticalAlign="end">
              {!!bestPricePackage && <>
                <OfferTilePricing
                  offer={offer}
                  hasDates={hasDates}
                  checkInDate={checkInDate}
                  pkg={bestPricePackage}
                  offerUrl={offerUrl}
                  align="start"
                  pricing={bestPriceForDates}
                  filters={filters}
                  showCompact
                />
                {!isMobile && <TextButton kind="primary">View offer</TextButton>}
              </>}
              {!(bestPriceForDates || bestPricePackage) && !fetchingPrice && <div><SoldOutMessage offer={offer} filters={filters} /></div>}
            </Group>
          </WalledContent>
        </ProductPaletteProvider>
      </Container>
    </Root>
  )
}

export default LeMapSearchTile
